'use client';

import { emitReviewsEvent, type ReviewsProvider } from '@/src/analytics/hotpads/events/reviews';
import dynamic from 'next/dynamic';
import { useApartmentReviewsContext } from './ApartmentReviewsProvider';
import { ReviewsShell, type ReviewSourceConfig } from './ReviewsShell';
import { ReviewsEntryPoint } from './ReviewsEntryPoint';

const RedditReviewsPanel = dynamic(() => import('./RedditReviewsPanel'));
const GoogleReviewsPanel = dynamic(() => import('./GoogleReviewsPanel'));
const ReputationApiPanel = dynamic(() => import('./ReputationApiPanel'));
export function ApartmentReviewsModule() {
  const { listing, lotId, reviews, googlePlaceId, redditTopics } = useApartmentReviewsContext();
  if (!reviews && !googlePlaceId && !redditTopics.length) return null;
  const sources: Array<ReviewSourceConfig> = [];
  const reputation = reviews?.reputation;
  if (reputation)
    sources.push({
      id: 'web',
      label: 'Web reviews',
      panel: {
        status: 'ready',
        render: () => (
          <ReputationApiPanel
            data={reputation}
            onInteraction={(interaction) => emitReviewsEvent(listing, interaction)}
          />
        ),
      },
    });
  if (googlePlaceId)
    sources.push({
      id: 'google',
      label: 'Google',
      instanceKey: googlePlaceId,
      eager: true,
      panel: {
        status: 'ready',
        render: () => <GoogleReviewsPanel placeId={googlePlaceId} />,
      },
    });
  if (redditTopics.length)
    sources.push({
      id: 'reddit',
      label: 'Reddit',
      panel: {
        status: 'ready',
        render: () => (
          <RedditReviewsPanel
            topics={redditTopics}
            onTopicClick={(topicId) =>
              emitReviewsEvent(listing, { event: 'reviews_reddit_topic_click', provider: 'reddit', topicId })
            }
          />
        ),
      },
    });
  return (
    <ReviewsShell
      key={`${listing?.aliasEncoded}:${lotId}`}
      id="reviews"
      sources={sources}
      onImpression={(provider, availableProviders) =>
        emitReviewsEvent(listing, { event: 'reviews_module_impression', provider, availableProviders })
      }
      onProviderChange={(provider, previousProvider) =>
        emitReviewsEvent(listing, { event: 'reviews_provider_change', provider, previousProvider })
      }
    />
  );
}

export function ApartmentReviewsEntry() {
  const { listing, reviews, googlePlaceId, redditTopics } = useApartmentReviewsContext();
  if (!reviews && !googlePlaceId && !redditTopics.length) return null;
  const { averageRating, totalReviews } = reviews?.reputation ?? {};
  return (
    <ReviewsEntryPoint
      targetId="reviews"
      onEntryClick={() => {
        const availableProviders: Array<ReviewsProvider> = [];
        if (reviews?.reputation) availableProviders.push('web');
        if (googlePlaceId) availableProviders.push('google');
        if (redditTopics.length) availableProviders.push('reddit');
        emitReviewsEvent(listing, { event: 'reviews_entry_click', availableProviders });
      }}
      hasReputation={!!reviews?.reputation}
      aggregateRating={
        averageRating !== undefined && totalReviews !== undefined
          ? { rating: averageRating, reviewCount: totalReviews }
          : null
      }
      hasGooglePlace={!!googlePlaceId}
      hasRedditLinks={redditTopics.length > 0}
    />
  );
}
